'use client'

import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import ProgressBarPresentation from './ProgressBarPresentation'

export interface ProgressBarProps {
  currentTime: number
  duration: number
  buffered?: number
  isSeekable?: boolean
  showTimeStamps?: boolean
  onSeek?: (time: number) => void
}

const ProgressBar: FC<ProgressBarProps> = ({
  currentTime,
  duration,
  buffered = 0,
  isSeekable = true,
  showTimeStamps = false,
  onSeek,
}) => {
  const seekBarRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const bufferedProgress = duration > 0 ? (buffered / duration) * 100 : 0

  // Format time for display
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  // Calculate seek position from mouse event
  const calculateSeekTime = useCallback(
    (clientX: number): number => {
      if (!seekBarRef.current) return currentTime

      const rect = seekBarRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
      const percentage = x / rect.width
      return percentage * duration
    },
    [currentTime, duration],
  )

  // Handle click to seek
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isSeekable || !onSeek) return

      const seekTime = calculateSeekTime(e.clientX)
      onSeek(seekTime)
    },
    [isSeekable, onSeek, calculateSeekTime],
  )

  // Handle drag to seek
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isSeekable || !onSeek) return

      setIsDragging(true)
      const seekTime = calculateSeekTime(e.clientX)
      onSeek(seekTime)
    },
    [isSeekable, onSeek, calculateSeekTime],
  )

  // Handle drag move
  useEffect(() => {
    if (!isDragging || !isSeekable || !onSeek) return

    const handleMouseMove = (e: MouseEvent) => {
      const seekTime = calculateSeekTime(e.clientX)
      onSeek(seekTime)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, isSeekable, onSeek, calculateSeekTime])

  return (
    <ProgressBarPresentation
      progress={progress}
      buffered={bufferedProgress}
      currentTimeDisplay={formatTime(currentTime)}
      durationDisplay={formatTime(duration)}
      // 残り時間。尺が未取得（duration が 0/NaN）の間は負や NaN にならないよう 0 に丸める。
      remainingTimeDisplay={formatTime(Math.max(0, (duration || 0) - currentTime))}
      showTimeStamps={showTimeStamps}
      isSeekable={isSeekable}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      seekBarRef={seekBarRef}
    />
  )
}

export default ProgressBar
